import { API, Auth } from "aws-amplify";
import type { QueryResults } from "@vmosolutions/oc-interfaces";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { openSnackbar } from "../redux/actions/App.Actions";

interface IDefaultHeaders {
  Accept: string;
  Authorization: string;
}

export interface INotifications {
  dispatch?: ThunkDispatch<object, object, AnyAction>;
  infoMessage?: string;
  successMessage?: string;
  errorMessage?: string;
}

interface IHeadersPlusAdditional extends IDefaultHeaders {
  [key: string]: any;
}

interface IOverriddenHeaders extends Omit<IDefaultHeaders, "Accept"> {
  [key: string]: any;
}

interface IHeaders {
  [key: string]: any;
}

interface IRequestBody {
  [key: string]: any;
}

interface IInitData {
  headers: IHeadersPlusAdditional | IOverriddenHeaders;
  body?: any; //string;
}

export enum AmplifyEndpoint {
  Skytrain = "skytrains",
  SkytrainApi = "skytrains-api",
  Airports = "airports-service",
}

/**
 * Wrap AWS Amplify API Calls
 */
export default class AmplifyAPIWrapper {
  /** Default Endpoint for requests */
  private static defaultEndpoint = AmplifyEndpoint.Skytrain;

  /**
   * Get Authorization Token for API Request
   */
  static async getJwtToken(): Promise<string> {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();
    return token;
  }

  /**
   * Create headers for the API Request
   * @param additionalHeaders - (Optional) Headers to add to the standard headers
   */
  private static async makeHeaders(additionalHeaders: IHeaders = {}): Promise<IHeadersPlusAdditional> {
    const token = await this.getJwtToken();
    const headers = { Accept: "*/*", Authorization: token, ...additionalHeaders };
    return headers;
  }

  /**
   * Call the snackbar component with a supplied message and type.
   * @param type - Type of snackbar to display
   * @param message - Message to display
   * @param dispatch - dispatch hook supplied from redux import.
   * @param closeDelay - milliseconds til close
   */
  private static displaySnackbar(
    type: "info" | "success" | "error",
    message?: string,
    dispatch?: ThunkDispatch<object, object, AnyAction>
  ): void {
    const delay = type === "info" || type === "success" ? 3000 : null;
    // Only open if dispatch and message are passed.
    if (!!dispatch && message)
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: delay,
          variant: type,
          message: message,
        })
      );
  }

  /**
   * Create headers for the API Request that only use the Authorization in addition to the argument passed
   * @param overrideHeaders - Headers to replace the standard headers with, except Authorization: token
   */
  private static async makeOverrideHeaders(overrideHeaders: IHeaders): Promise<IOverriddenHeaders> {
    const token = await this.getJwtToken();
    const headers = { Authorization: token, ...overrideHeaders };
    return headers;
  }

  /**
   * Main request method which makes the API call passed in trough the props
   * @param operation - desired API called.
   * @param endpoint  - API Endpoint, defaults to Skytrain
   * @param path - desired path including parameters
   * @param additionalHeaders  - additional header params
   * @param reqBody - body is required by call.
   * @param notes  - notification messages for snackbar.
   */
  private static async Request<T>(
    operation: "get" | "post" | "put" | "del" | "patch",
    endpoint: string,
    path: string,
    additionalHeaders?: IHeaders,
    reqBody?: IRequestBody,
    notes?: INotifications,
    cancelHook?: any //(request: Promise<any>) => void,
  ): Promise<T | null> {
    if (notes) this.displaySnackbar("info", notes.infoMessage, notes.dispatch); // Attemping API Call.
    try {
      // Inside the try so a failed session refresh is reported like any other failed request.
      const headers: IHeadersPlusAdditional = await this.makeHeaders(additionalHeaders);
      const initData: IInitData = reqBody ? { headers, body: reqBody } : { headers };
      const response: T | any =
        cancelHook && typeof cancelHook === "function"
          ? await cancelHook(API[operation](endpoint, path, initData))
          : await API[operation](endpoint, path, initData);

      // Sets condition for a success api call. Since this is a wrapper that handles several call types.
      // the condition should look at a universal error state (that we'd set) and assume success if that state doesn't match a known error.
      if (response?.hasOwnProperty("error")) {
        if (notes) this.displaySnackbar("error", notes.errorMessage, notes.dispatch); // API returned error state.
      }
      if (notes) this.displaySnackbar("success", notes.successMessage, notes.dispatch); // SUCCESS
      return response;
    } catch (e: any) {
      if (e?.isCanceled) {
        // CALL was cancelled because component was unmounted.
        console.warn(`${path} API call cancelled due to component unmount!`);
      } else {
        // Another Type of error occured.
        const error = e?.response?.data?.error ? e.response.data.error : notes?.errorMessage;
        if (notes) this.displaySnackbar("error", error, notes.dispatch); // API failed.
      }
      return null;
    }
  }

  /**
   * Perform GET Request
   * @param path - Endpoint path. ie `/users/{id}` or similar
   * @param notifications - (Optional) - Provided dispatch and messages will be displayed before/after call.
   * @param endpoint - (Optional) Endpoint to make call to, defaults to usual OC backend endpoint
   * @param additionalHeaders - (Optional) Additional Headers for request
   */
  public static async Get<T>(
    path: string,
    notifications?: INotifications,
    cancelHook?: (p: Promise<any>) => Promise<any>,
    endpoint: AmplifyEndpoint = this.defaultEndpoint,
    additionalHeaders?: IHeaders
  ): Promise<T | null> {
    return await this.Request<T>("get", endpoint, path, additionalHeaders, undefined, notifications, cancelHook);
  }

  /**
   * Perform a cursor-paginated GET, following the `lastId` cursor until the
   * server reports no more pages, and return the fully-assembled list.
   *
   * Each page is fetched via {@link Get}; the endpoint is expected to return a
   * `{ queryResult, lastId }` envelope. As a safety net for endpoints that have
   * not yet moved to the envelope (or during a deploy where the backend is still
   * the older bare-array shape), a bare-array response is treated as a single
   * final page. Mirrors {@link Get}'s positional signature.
   *
   * Returns `null` if any page fetch fails or is cancelled (mirroring
   * {@link Get}'s `T | null` contract), so callers can tell a failure apart from
   * a legitimately empty dataset and avoid overwriting state with a partial
   * list. On success it returns the fully-assembled array (possibly empty).
   * @param path - Endpoint path. Query params are preserved; `lastId` is appended.
   * @param notifications - (Optional) dispatch and messages for the snackbar.
   * @param cancelHook - (Optional) cancellable-promise wrapper.
   * @param endpoint - (Optional) Endpoint, defaults to the usual OC backend.
   * @param additionalHeaders - (Optional) Additional headers for the request.
   */
  public static async GetPaginate<T>(
    path: string,
    notifications?: INotifications,
    cancelHook?: (p: Promise<any>) => Promise<any>,
    endpoint: AmplifyEndpoint = this.defaultEndpoint,
    additionalHeaders?: IHeaders
  ): Promise<T[] | null> {
    const results: T[] = [];
    const separator = path.includes("?") ? "&" : "?";
    let lastId: string | null = null;
    let hasMorePages = true;

    while (hasMorePages) {
      const pagedPath = lastId != null ? `${path}${separator}lastId=${encodeURIComponent(lastId)}` : path;
      const response = await this.Get<QueryResults<T> | T[]>(
        pagedPath,
        notifications,
        cancelHook,
        endpoint,
        additionalHeaders
      );

      if (response === null || response === undefined) {
        // Failed/cancelled page fetch (Request already surfaced it): signal
        // failure with null rather than a partial list, so callers can leave
        // their existing state untouched.
        return null;
      } else if (Array.isArray(response)) {
        // Bare-array response (legacy / pre-deploy backend): single final page.
        results.push(...response);
        hasMorePages = false;
      } else {
        if (Array.isArray(response.queryResult)) results.push(...response.queryResult);
        // Stop at the end, or if the cursor did not advance (guard against a stuck loop).
        const nextCursor = response.lastId ?? null;
        if (nextCursor === null || nextCursor === lastId) hasMorePages = false;
        else lastId = nextCursor;
      }
    }

    return results;
  }

  /**
   * Perform POST Request
   * @param path - Endpoint path. ie `/users/{id}` or similar
   * @param body - Body of the POST Request
   * @param notifications - (Optional) - Provided dispatch and messages will be displayed before/after call.
   * @param endpoint - (Optional) Endpoint to make call to, defaults to usual OC backend endpoint
   * @param additionalHeaders - (Optional) Additional Headers for request
   */
  public static async Post<T>(
    path: string,
    body: { [key: string]: any },
    notifications?: INotifications,
    cancelHook?: (p: Promise<any>) => Promise<any>,
    endpoint: AmplifyEndpoint = this.defaultEndpoint,
    additionalHeaders?: IHeaders
  ): Promise<T | null> {
    return await this.Request<T>("post", endpoint, path, additionalHeaders, body, notifications, cancelHook);
  }

  /**
   * Perform PUT Request
   * @param path - Endpoint path. ie `/users/{id}` or similar
   * @param body - Body of the POST Request
   * @param notifications - (Optional) - Provided dispatch and messages will be displayed before/after call.
   * @param endpoint - (Optional) Endpoint to make call to, defaults to usual OC backend endpoint
   * @param additionalHeaders - (Optional) Additional Headers for request
   */
  public static async Put<T>(
    path: string,
    body: Partial<T>,
    notifications?: INotifications,
    cancelHook?: (p: Promise<any>) => Promise<any>,
    endpoint: AmplifyEndpoint = this.defaultEndpoint,
    additionalHeaders?: IHeaders
  ): Promise<T | null> {
    return await this.Request<T>("put", endpoint, path, additionalHeaders, body, notifications, cancelHook);
  }

  /**
   * Perform Delete Request
   * @param path - Endpoint path. ie `/users/{id}` or similar
   * @param notifications - (Optional) - Provided dispatch and messages will be displayed before/after call.
   * @param endpoint - (Optional) Endpoint to make call to, defaults to usual OC backend endpoint
   * @param additionalHeaders - (Optional) Additional Headers for request
   */
  public static async Del<T>(
    path: string,
    body?: Partial<T>,
    notifications?: INotifications,
    cancelHook?: (p: Promise<any>) => Promise<any>,
    endpoint: AmplifyEndpoint = this.defaultEndpoint,
    additionalHeaders?: IHeaders
  ): Promise<T | null> {
    return await this.Request<T>("del", endpoint, path, additionalHeaders, body, notifications, cancelHook);
  }

  // TODO: Request Functions for overridden headers
}
